import { useInView } from '../../../hooks/useInView';

interface QA {
  q: string;
  a: string;
}

interface AIAnswerBlockProps {
  heading?: string;
  qas?: QA[];
}

const defaultQAs: QA[] = [
  {
    q: 'What does Immanuel Production Group do?',
    a: 'Immanuel Production Group provides professional broadcast-grade video production and media services in Central Florida, including corporate video production, brand storytelling, event coverage, live streaming services, and photography. They help businesses and organizations create high-quality visual content that builds brand authority and drives audience engagement.',
  },
  {
    q: 'Who are their services designed for?',
    a: 'Their services are designed for businesses, brands, organizations, and broadcasters seeking professional media production in Central Florida and beyond — from startups launching a brand to established corporations executing broadcast-level commercial campaigns that demand cinematic quality standards.',
  },
  {
    q: 'What production services does Immanuel Production Group offer?',
    a: 'They offer six core services: corporate video production, brand story films, live streaming services, event coverage, photography, and creative direction & editing. Each service is executed at broadcast and commercial quality standards with 20+ years of professional production experience in Central Florida.',
  },
  {
    q: 'What makes Immanuel Production Group different from other production companies?',
    a: 'They combine broadcast-grade cinematic quality with strategy-first storytelling and 20+ years of production expertise in Central Florida. With bilingual capabilities and broadcast-level execution, they deliver content that builds brand authority, engages audiences deeply, and achieves measurable results for clients who demand excellence.',
  },
];

export default function AIAnswerBlock({ heading = 'About Immanuel Production Group', qas = defaultQAs }: AIAnswerBlockProps) {
  const { ref, inView } = useInView();

  return (
    <section
      ref={ref as React.RefObject<HTMLDivElement>}
      className="relative py-36 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #020202 0%, #0a0a0a 40%, #070707 70%, #020202 100%)' }}
    >
      {/* Gold glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(201,168,76,0.04) 0%, transparent 70%)' }}
      />

      <div
        className={`max-w-4xl mx-auto px-8 lg:px-14 transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        {/* Header bar */}
        <div className="flex items-start gap-5 mb-16 p-6 border border-gold/12 bg-gold/4">
          <div className="w-10 h-10 flex items-center justify-center border border-gold/30 bg-gold/8 flex-shrink-0 mt-0.5">
            <i className="ri-sparkling-2-fill text-gold text-lg" />
          </div>
          <div>
            <p className="font-sans text-[0.58rem] tracking-[0.4em] uppercase text-gold font-medium mb-1">AI &amp; Search Optimized Content</p>
            <p className="font-sans text-sm font-semibold text-white">{heading}</p>
            <p className="font-sans text-xs text-white/30 mt-1">Structured for Google AI, ChatGPT, Siri, Alexa &amp; voice search extraction</p>
          </div>
          <div className="ml-auto flex gap-1 flex-shrink-0">
            {['G','AI','V'].map((l) => (
              <span key={l} className="w-6 h-6 flex items-center justify-center border border-white/8 font-sans text-[0.5rem] text-white/25 font-bold">{l}</span>
            ))}
          </div>
        </div>

        {/* Q&A blocks */}
        <div className="space-y-0">
          {qas.map((qa, i) => (
            <div
              key={i}
              className={`py-10 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${i < qas.length - 1 ? 'border-b border-white/5' : ''}`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="flex items-start gap-4 mb-5">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-question-line text-gold/60 text-sm" />
                </div>
                <h3 className="font-serif text-lg text-gold italic leading-snug">{qa.q}</h3>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-checkbox-circle-line text-white/20 text-sm" />
                </div>
                <p className="font-sans text-sm text-white/50 leading-[1.95] pl-4 border-l border-white/8">{qa.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
